import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Paper, Typography, Box,
  IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Chip,
} from '@mui/material';
import {
  Close, Send as SendIcon, CheckCircle,
  Thermostat, Favorite, MonitorHeart, Air,
  FitnessCenter, Height, Calculate, BloodtypeOutlined,
  WaterDrop, AccessTime, CalendarToday,
  Clear as ClearIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { loadPatientsVitals, savePatienVitals } from '../../BaseURL';
import axios from 'axios';
import moment from 'moment';
import { successMessage } from '../../Message';

// ── Remark colour map ─────────────────────────────────────────────────
const REMARK_COLOR = {
  Normal:               { bg: '#D1FAE5', color: '#065F46' },
  Fever:                { bg: '#FEE2E2', color: '#991B1B' },
  Hypothermia:          { bg: '#DBEAFE', color: '#1E40AF' },
  Tachycardia:          { bg: '#FEE2E2', color: '#991B1B' },
  Bradycardia:          { bg: '#FFF3CD', color: '#7C4A03' },
  'High Blood Pressure':{ bg: '#FEE2E2', color: '#991B1B' },
  'Low Blood Pressure': { bg: '#DBEAFE', color: '#1E40AF' },
  Tachypnea:            { bg: '#FEE2E2', color: '#991B1B' },
  Bradypnea:            { bg: '#DBEAFE', color: '#1E40AF' },
  Underweight:          { bg: '#FFF3CD', color: '#7C4A03' },
  Overweight:           { bg: '#FFF3CD', color: '#7C4A03' },
  Obese:                { bg: '#FEE2E2', color: '#991B1B' },
  'High FBS':           { bg: '#FEE2E2', color: '#991B1B' },
  'Low FBS':            { bg: '#DBEAFE', color: '#1E40AF' },
  'High RBS':           { bg: '#FEE2E2', color: '#991B1B' },
  'Low RBS':            { bg: '#DBEAFE', color: '#1E40AF' },
  Invalid:              { bg: '#F3F4F6', color: '#6B7280' },
};

const RemarkChip = ({ value }) => {
  if (!value) return null;
  const s = REMARK_COLOR[value] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <Chip label={value} size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: s.bg, color: s.color, border: 'none', height: 20 }} />
  );
};

// ── Single vital row ──────────────────────────────────────────────────
const VitalRow = ({ icon, label, unit, value, onChange, remark, error, disabled = false }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5,
    px: 1.5, py: 1, borderRadius: 2,
    border: '1px solid #E5E7EB', bgcolor: '#FAFAFA',
    transition: 'all 0.18s',
    '&:hover': { borderColor: '#11998e', bgcolor: '#F0FDF9', boxShadow: '0 2px 8px rgba(17,153,142,0.08)' },
  }}>
    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#F0FDF9',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </Box>

    <Box sx={{ minWidth: 130, flexShrink: 0 }}>
      <Typography variant="body2" fontWeight={700} color="#1A202C" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="#9CA3AF" sx={{ fontSize: '0.67rem' }}>{unit}</Typography>
    </Box>

    <Box sx={{ flex: 1 }}>
      <TextField
        fullWidth size="small" variant="outlined"
        value={value} onChange={onChange}
        disabled={disabled}
        error={!!error} helperText={error}
        FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.3, mx: 0 } }}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '0.82rem', fontWeight: 600,
            '& fieldset': { borderColor: error ? '#EF4444' : '#E5E7EB' },
            '&:hover fieldset': { borderColor: '#11998e' },
            '&.Mui-focused fieldset': { borderColor: '#11998e' },
            '&.Mui-disabled': { bgcolor: '#F9FAFB' },
            '& input': { py: '6px', px: '10px' },
          },
        }}
      />
    </Box>

    <Box sx={{ minWidth: 82, textAlign: 'right', flexShrink: 0 }}>
      <RemarkChip value={remark} />
    </Box>
  </Box>
);

// ═════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════
const PatientVitals = ({ open, onClose, info }) => {
  const getCurrentDate = () => new Date().toISOString().split('T')[0];
  const getCurrentTime = () =>
    new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });

  const Todaydate = moment().format('YYYY-MM-DD');

  const blankVitals = {
    temperature: '', temperatureRemarks: '',
    pulse: '',       pulseRemarks: '',
    bloodPressure: '', bloodPressureRemarks: '',
    respirationRate: '', respirationRateRemarks: '',
    weight: '', height: '',
    bmi: '',   bmiRemarks: '',
    fbs: '',   fbsRemarks: '',
    rbs: '',   rbsRemarks: '',
    spo2: '',
    time: getCurrentTime(),
    date: getCurrentDate(),
  };

  const [patientData, setPatientData] = useState({ field1: '', field2: '', field3: '', field4: '', ...blankVitals });
  const [errors, setErrors]           = useState({});
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [id, setId]                   = useState();

  useEffect(() => {
    if (info) {
      setPatientData(prev => ({
        ...prev,
        field1: info.id ?? '', field2: info.name ?? '',
        field3: info.gender ?? '', field4: info.age ?? '',
      }));
      setId(info.id);
    }
  }, [info]);

  useEffect(() => { if (id) loadVitalInfo(); }, [id]);

  const loadVitalInfo = async () => {
    try {
      const res = await axios.post(loadPatientsVitals, { id });
      if (res.data.success) {
        setVitalsHistory(res.data.results);
        const today = res.data.results.find(r => r.dat_vist === Todaydate);
        if (today) fillFromRecord(today);
      }
    } catch (err) {
      console.error('Error loading vitals:', err);
      await Swal.fire({ title: 'Error', text: 'Failed to load vitals', icon: 'error' });
    }
  };

  const fillFromRecord = (rec) => {
    setPatientData(prev => ({
      ...prev,
      temperature: rec.tem || '',         temperatureRemarks: rec.tem_rem || '',
      pulse: rec.pulse || '',              pulseRemarks: rec.puls_rem || '',
      bloodPressure: rec.pre || '',        bloodPressureRemarks: rec.pre_rem || '',
      respirationRate: rec.res || '',      respirationRateRemarks: rec.res_rem || '',
      weight: rec.we || '',                height: rec.high || '',
      bmi: rec.bmi || '',                  bmiRemarks: rec.BMIRemarks || '',
      fbs: rec.fbs || '',                  fbsRemarks: rec.fbs_rem || '',
      rbs: rec.rbs || '',                  rbsRemarks: rec.rbs_rem || '',
      spo2: rec.spo2 || '',
      time: getCurrentTime(),              date: getCurrentDate(),
    }));
    setErrors({});
  };

  // ── Validation ────────────────────────────────────────────────────
  const validateField = (field, value) => {
    if (!value || value === '') return '';
    if (field === 'temperature') {
      if (!/^\d*\.?\d*$/.test(value)) return 'Valid number only';
      const n = parseFloat(value);
      if (n < 30) return 'Too low (min 30°C)';
      if (n > 45) return 'Too high (max 45°C)';
    }
    if (field === 'pulse') {
      if (!/^\d+$/.test(value)) return 'Whole number only';
      const n = parseInt(value);
      if (n < 30)  return 'Too low (min 30 bpm)';
      if (n > 200) return 'Too high (max 200 bpm)';
    }
    if (field === 'bloodPressure') {
      if (!value.includes('/')) return 'Format: 120/80';
      const [s, d] = value.split('/').map(Number);
      if (isNaN(s) || isNaN(d)) return 'Invalid values';
      if (s <= d) return 'Systolic must be > diastolic';
      if (s > 250 || d > 150) return 'Too high';
    }
    if (field === 'respirationRate') {
      const n = parseInt(value);
      if (n < 8)  return 'Too low (min 8)';
      if (n > 40) return 'Too high (max 40)';
    }
    if (field === 'fbs') {
      const n = parseFloat(value);
      if (isNaN(n)) return 'Invalid';
      if (n < 1)  return 'Too low';
      if (n > 30) return 'Too high';
    }
    if (field === 'rbs') {
      const n = parseFloat(value);
      if (isNaN(n)) return 'Invalid';
      if (n < 1)  return 'Too low';
      if (n > 40) return 'Too high';
    }
    return '';
  };

  // ── Auto remarks ──────────────────────────────────────────────────
  const generateRemark = (field, value) => {
    if (!value && value !== 0) return '';
    if (field === 'temperature') {
      const n = parseFloat(value);
      if (isNaN(n)) return 'Invalid';
      return n < 36 ? 'Hypothermia' : n > 37.5 ? 'Fever' : 'Normal';
    }
    if (field === 'pulse') {
      const n = Number(value);
      return n < 60 ? 'Bradycardia' : n > 100 ? 'Tachycardia' : 'Normal';
    }
    if (field === 'bloodPressure') {
      if (!String(value).includes('/')) return 'Invalid BP';
      const [s, d] = String(value).split('/').map(Number);
      if (isNaN(s) || isNaN(d)) return 'Invalid BP';
      if (s < 90 || d < 60)   return 'Low Blood Pressure';
      if (s > 140 || d > 90)  return 'High Blood Pressure';
      return 'Normal';
    }
    if (field === 'respirationRate') {
      const n = Number(value);
      return n < 12 ? 'Bradypnea' : n > 20 ? 'Tachypnea' : 'Normal';
    }
    if (field === 'bmi') {
      const n = parseFloat(value);
      if (isNaN(n)) return '';
      if (n < 18.5) return 'Underweight';
      if (n < 25)   return 'Normal';
      if (n < 30)   return 'Overweight';
      return 'Obese';
    }
    if (field === 'fbs') {
      const n = parseFloat(value);
      if (isNaN(n)) return 'Invalid';
      if (n < 3.9)  return 'Low FBS';
      if (n > 7.0)  return 'High FBS';
      return 'Normal';
    }
    if (field === 'rbs') {
      const n = parseFloat(value);
      if (isNaN(n)) return 'Invalid';
      if (n < 3.9)  return 'Low RBS';
      if (n > 11.1) return 'High RBS';
      return 'Normal';
    }
    return '';
  };

  // ── Change handler ────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const input = e.target.value;
    const err   = validateField(field, input);
    setErrors(prev => ({ ...prev, [field]: err }));

    setPatientData(prev => {
      const next = { ...prev, [field]: input };

      const autoRemark = ['temperature', 'pulse', 'bloodPressure', 'respirationRate', 'fbs', 'rbs'];
      if (autoRemark.includes(field)) {
        next[`${field}Remarks`] = err ? 'Invalid' : generateRemark(field, input);
      }

      if (field === 'weight' || field === 'height') {
        const w = parseFloat(field === 'weight' ? input : prev.weight);
        const h = parseFloat(field === 'height' ? input : prev.height);
        if (!isNaN(w) && !isNaN(h) && h > 0) {
          const bmi = +(w / ((h / 100) ** 2)).toFixed(1);
          next.bmi        = String(bmi);
          next.bmiRemarks = generateRemark('bmi', bmi);
        } else { next.bmi = ''; next.bmiRemarks = ''; }
      }
      return next;
    });
  };

  const handleClear = () => {
    setPatientData(prev => ({ ...prev, ...blankVitals, time: getCurrentTime(), date: getCurrentDate() }));
    setErrors({});
  };

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    const req = {};
    if (!patientData.temperature?.trim()) req.temperature = 'Temperature is required';
    if (!patientData.pulse?.trim())       req.pulse       = 'Pulse is required';
    if (Object.keys(req).length > 0) { setErrors(prev => ({ ...prev, ...req })); return; }

    try {
      const payload = {
        field1: patientData.field1, field2: patientData.field2,
        field3: patientData.field3, field4: patientData.field4,
        temperature: patientData.temperature,         temperatureRemarks: patientData.temperatureRemarks,
        pulse: patientData.pulse,                     pulseRemarks: patientData.pulseRemarks,
        bloodPressure: patientData.bloodPressure,     bloodPressureRemarks: patientData.bloodPressureRemarks,
        respirationRate: patientData.respirationRate, respirationRateRemarks: patientData.respirationRateRemarks,
        weight: patientData.weight,                   height: patientData.height,
        bmi: patientData.bmi,                         bmiRemarks: patientData.bmiRemarks,
        fbs: patientData.fbs,                         fbsRemarks: patientData.fbsRemarks,
        rbs: patientData.rbs,                         rbsRemarks: patientData.rbsRemarks,
        spo2: patientData.spo2,
        time: patientData.time,                       date: patientData.date,
      };
      const res = await axios.post(savePatienVitals, payload);
      if (res.data.success) {
        successMessage('Save', res.data.message);
        handleClear();
        onClose();
      }
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to Save', icon: 'error' });
    }
  };

  // ── Vital rows definition ─────────────────────────────────────────
  const vitalRows = [
    { icon: <Thermostat sx={{ fontSize: 17, color: '#EF4444' }} />,       label: 'Temperature',      unit: '°C',               fieldKey: 'temperature',     remark: patientData.temperatureRemarks    },
    { icon: <Favorite sx={{ fontSize: 17, color: '#EC4899' }} />,          label: 'Pulse',            unit: 'bpm',              fieldKey: 'pulse',           remark: patientData.pulseRemarks          },
    { icon: <MonitorHeart sx={{ fontSize: 17, color: '#DC2626' }} />,      label: 'Blood Pressure',   unit: 'mmHg (sys/dia)',   fieldKey: 'bloodPressure',   remark: patientData.bloodPressureRemarks  },
    { icon: <Air sx={{ fontSize: 17, color: '#06B6D4' }} />,               label: 'Respiration Rate', unit: 'CPM',              fieldKey: 'respirationRate', remark: patientData.respirationRateRemarks},
    { icon: <FitnessCenter sx={{ fontSize: 17, color: '#8B5CF6' }} />,     label: 'Weight',           unit: 'kg',               fieldKey: 'weight',          remark: null                              },
    { icon: <Height sx={{ fontSize: 17, color: '#10B981' }} />,            label: 'Height',           unit: 'cm',               fieldKey: 'height',          remark: null                              },
    { icon: <Calculate sx={{ fontSize: 17, color: '#6366F1' }} />,         label: 'BMI',              unit: 'kg/m² (auto)',     fieldKey: 'bmi',             remark: patientData.bmiRemarks,  disabled: true },
    { icon: <WaterDrop sx={{ fontSize: 17, color: '#F59E0B' }} />,         label: 'FBS',              unit: 'mmol/L (fasting)', fieldKey: 'fbs',             remark: patientData.fbsRemarks            },
    { icon: <WaterDrop sx={{ fontSize: 17, color: '#D97706' }} />,         label: 'RBS',              unit: 'mmol/L (random)',  fieldKey: 'rbs',             remark: patientData.rbsRemarks            },
    { icon: <BloodtypeOutlined sx={{ fontSize: 17, color: '#3B82F6' }} />, label: 'SpO₂',             unit: '%',                fieldKey: 'spo2',            remark: null                              },
  ];

  const latest = vitalsHistory[0];
  const summaryItems = latest ? [
    { label: 'Temp',  value: latest.tem   ? `${latest.tem}°C`       : '—', color: '#EF4444' },
    { label: 'Pulse', value: latest.pulse ? `${latest.pulse} bpm`   : '—', color: '#EC4899' },
    { label: 'BP',    value: latest.pre   || '—',                           color: '#DC2626' },
    { label: 'FBS',   value: latest.fbs   ? `${latest.fbs} mmol/L` : '—', color: '#F59E0B' },
    { label: 'RBS',   value: latest.rbs   ? `${latest.rbs} mmol/L` : '—', color: '#D97706' },
    { label: 'SpO₂',  value: latest.spo2  ? `${latest.spo2}%`      : '—', color: '#3B82F6' },
  ] : [];

  return (
    <Dialog open={open} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '92vh', maxHeight: '92vh', display: 'flex', flexDirection: 'column' } }}>

      {/* ══ HEADER (fixed, never scrolls) ═══════════════════════════ */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Box sx={{
          background: 'linear-gradient(135deg,#0F766E 0%,#11998e 55%,#38ef7d 100%)',
          px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="white" letterSpacing={0.3}>
              Patient Vitals Recording
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Record and track patient vital signs
            </Typography>
          </Box>
          <IconButton onClick={onClose}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <Close />
          </IconButton>
        </Box>

        {/* Patient info strip */}
        <Box sx={{
          bgcolor: '#F8FAFB', borderBottom: '1px solid #E5E7EB',
          px: 3, py: 1.2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center',
        }}>
          {[
            { label: 'Patient ID', value: patientData.field1 },
            { label: 'Name',       value: patientData.field2 },
            { label: 'Gender',     value: patientData.field3 },
            { label: 'Age',        value: patientData.field4 },
          ].map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="#9CA3AF" fontWeight={600}
                textTransform="uppercase" letterSpacing={0.4} sx={{ fontSize: '0.68rem' }}>
                {f.label}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="#1A202C">{f.value || '—'}</Typography>
            </Box>
          ))}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <CalendarToday sx={{ fontSize: 13, color: '#11998e' }} />
            <Typography variant="caption" fontWeight={600} color="#11998e">{patientData.date}</Typography>
            <AccessTime sx={{ fontSize: 13, color: '#9CA3AF', ml: 0.5 }} />
            <Typography variant="caption" color="#9CA3AF">{patientData.time}</Typography>
          </Box>
        </Box>
      </DialogTitle>

      {/* ══ BODY (takes remaining height, overflow hidden — each panel scrolls itself) ══ */}
      <DialogContent sx={{ p: 0, bgcolor: '#F8FAFB', overflow: 'hidden', flex: 1, display: 'flex' }}>
        <Grid container sx={{ flex: 1, overflow: 'hidden' }}>

          {/* ── LEFT: Vital inputs — scrolls independently ── */}
          <Grid item xs={12} md={5} sx={{
            p: 2.5,
            borderRight: '1px solid #E5E7EB',
            height: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
          }}>
            {/* Sticky sub-header inside left panel */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              mb: 1.5, position: 'sticky', top: 0, zIndex: 1,
              bgcolor: '#F8FAFB', py: 0.5,
            }}>
              <Typography variant="subtitle2" fontWeight={800} color="#1A202C">Enter Vitals</Typography>
              <Button size="small" startIcon={<ClearIcon sx={{ fontSize: 14 }} />} onClick={handleClear}
                variant="outlined"
                sx={{
                  textTransform: 'none', fontSize: '0.73rem',
                  borderColor: '#E5E7EB', color: '#6B7280', borderRadius: 1.5, py: 0.25,
                  '&:hover': { borderColor: '#11998e', color: '#11998e', bgcolor: '#F0FDF9' },
                }}>
                Clear
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
              {vitalRows.map(({ icon, label, unit, fieldKey, remark, disabled }) => (
                <VitalRow
                  key={fieldKey}
                  icon={icon} label={label} unit={unit}
                  value={patientData[fieldKey] ?? ''}
                  onChange={handleChange(fieldKey)}
                  remark={remark}
                  error={errors[fieldKey]}
                  disabled={disabled}
                />
              ))}
            </Box>
          </Grid>

          {/* ── RIGHT: History — scrolls independently ── */}
          <Grid item xs={12} md={7} sx={{
            p: 2.5,
            height: '100%',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <Typography variant="subtitle2" fontWeight={800} color="#1A202C" sx={{ mb: 1.5, flexShrink: 0 }}>
              Vitals History
            </Typography>

            {/* Table */}
            <TableContainer component={Paper} elevation={0}
              sx={{
                flex: 1, border: '1px solid #E5E7EB', borderRadius: 2,
                overflow: 'auto', minHeight: 0,
                '&::-webkit-scrollbar': { width: '5px', height: '5px' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
              }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['Date','Time','Temp','Pulse','BP','RR','Wt','Ht','BMI','FBS','RBS','SpO₂','Use'].map(h => (
                      <TableCell key={h} sx={{
                        fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap',
                        bgcolor: '#11998e', color: 'white', borderBottom: 'none', py: 1,
                      }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vitalsHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} sx={{ textAlign: 'center', py: 6, color: '#9CA3AF', fontSize: '0.82rem' }}>
                        No vitals history found
                      </TableCell>
                    </TableRow>
                  ) : vitalsHistory.map((rec, idx) => (
                    <TableRow key={rec.trans_code || idx} hover
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: '#F9FAFB' },
                        '&:hover': { bgcolor: 'rgba(17,153,142,0.05)' },
                      }}>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{rec.dat_vist}</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem',  color: '#6B7280', whiteSpace: 'nowrap' }}>{rec.time}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          {rec.tem}
                          {rec.tem_rem && (
                            <Chip label={rec.tem_rem} size="small"
                              sx={{ fontSize: '0.6rem', height: 16,
                                ...(REMARK_COLOR[rec.tem_rem] || { bg: '#F3F4F6', color: '#374151' }) }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.pulse}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{rec.pre}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.res}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.we}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.high}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.bmi}</TableCell>
                      <TableCell sx={{
                        fontSize: '0.72rem',
                        fontWeight: parseFloat(rec.fbs) > 7 ? 700 : 400,
                        color: parseFloat(rec.fbs) > 7 ? '#DC2626' : '#374151',
                      }}>{rec.fbs || '—'}</TableCell>
                      <TableCell sx={{
                        fontSize: '0.72rem',
                        fontWeight: parseFloat(rec.rbs) > 11.1 ? 700 : 400,
                        color: parseFloat(rec.rbs) > 11.1 ? '#DC2626' : '#374151',
                      }}>{rec.rbs || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{rec.spo2 || '—'}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Tooltip title="Fill form with this record">
                          <IconButton size="small" onClick={() => fillFromRecord(rec)}
                            sx={{ color: '#11998e', '&:hover': { bgcolor: 'rgba(17,153,142,0.1)' }, p: 0.4 }}>
                            <CheckCircle sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Latest summary strip */}
            {summaryItems.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                {summaryItems.map((s, i) => (
                  <Box key={i} sx={{
                    flex: '1 1 72px', p: 1, borderRadius: 2, textAlign: 'center',
                    border: `1px solid ${s.color}22`, bgcolor: `${s.color}08`,
                  }}>
                    <Typography variant="caption" color="#9CA3AF" fontWeight={600}
                      textTransform="uppercase" letterSpacing={0.4}
                      sx={{ display: 'block', fontSize: '0.6rem' }}>
                      {s.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={800} color={s.color} sx={{ fontSize: '0.76rem' }}>
                      {s.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* ══ FOOTER (fixed, never scrolls) ═══════════════════════════ */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid #E5E7EB', gap: 2, flexShrink: 0 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ textTransform: 'none', borderColor: '#E5E7EB', color: '#6B7280', borderRadius: 2, px: 3 }}>
          Cancel
        </Button>
        <Button startIcon={<SendIcon />} variant="contained" onClick={handleSave}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 4,
            background: 'linear-gradient(135deg,#0F766E,#11998e)',
            '&:hover': { background: 'linear-gradient(135deg,#0D6B63,#0F8F82)' },
          }}>
          Save Vitals
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientVitals;